import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../Config';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ImageSelectDialog from '../Components/ImageSelectDialog';

function Category() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showImageSelectDialog, setShowImageSelectDialog] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  // Form state (Add only)
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    label: '',
    image: '',
    showSubCategory: false
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/categories?disable_pagination=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      } else {
        throw new Error('Failed to fetch categories');
      }
    } catch (error) {
      toast.error(error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      let imageUrl = formData.image;

      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);

        const uploadResponse = await fetch('https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload', {
          method: 'POST',
          headers: {
            'X-Client': 'ADMIN',
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          imageUrl = uploadData[0];
          toast.success('Image uploaded successfully!');
        } else {
          toast.error('Failed to upload image');
          throw new Error('Failed to upload image');
        }
      }

      const url = isEditing 
        ? `${config.apiUrl}/api/v1/admin/categories/${editingId}`
        : `${config.apiUrl}/api/v1/admin/categories`;
        
      const method = isEditing ? 'PUT' : 'POST';

      formData.image = imageUrl;

      // Make all empty entries in the formData null
      Object.keys(formData).forEach((key) => {
        if (typeof formData[key] === "string" && formData[key].trim() === "") {
          formData[key] = null;
        }
      });
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...formData })
      });

      if (response.ok) {
        toast.success(isEditing ? 'Category updated successfully!' : 'Category created successfully!');
        fetchCategories();
        resetForm();
        setShowModal(false);
      } else {
        throw new Error('Failed to save category');
      }
    } catch (error) {
      toast.error(error.message);
      setError(error.message);
    }
  };

  // const handleDelete = async (categoryId) => {
  //   if (!window.confirm('Are you sure you want to delete this category?')) return;
    
  //   try {
  //     const token = localStorage.getItem('token');
  //     const response = await fetch(`${config.apiUrl}/api/v1/admin/categories/${categoryId}`, {
  //       method: 'DELETE',
  //       headers: {
  //         'Authorization': `Bearer ${token}`
  //       }
  //     });

  //     if (response.ok) {
  //       toast.success('Category deleted successfully!');
  //       fetchCategories();
  //     } else {
  //       throw new Error('Failed to delete category');
  //     }
  //   } catch (error) {
  //     toast.error(error.message);
  //     setError(error.message);
  //   }
  // };

  const handleEdit = (category) => {
    navigate(`/categories/${category.category_id}`);
  };

  const handleShowDetails = (category) => {
    setSelectedCategory(category);
    setShowDetailsModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      label: '',
      image: '',
      showSubCategory: false
    });
    setImageFile(null);
    setImagePreview('');
    setIsEditing(false);
    setEditingId(null);
  };

  const handleImageSelect = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageFile(file);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Topbar />
      <ToastContainer position="top-right" autoClose={3000} />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Add Category
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-100 border-l-4 border-red-500 p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title*</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtitle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Show Subcategories</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {categories.map((category) => (
                  <tr key={category.category_id} className="hover:bg-gray-50">
                    <td 
                      className="text-left px-6 py-4 cursor-pointer text-indigo-600 hover:text-indigo-900"
                      onClick={() => handleShowDetails(category)}
                    >
                      {category.title}
                    </td>
                    <td className="text-left px-6 py-4 text-gray-500">{category.subtitle || '-'}</td>
                    <td className="text-left px-6 py-4 text-gray-500">{category.label || '-'}</td>
                    <td className="text-left px-6 py-4 text-gray-500">
                      {category.showSubCategory ? 'Yes' : 'No'}
                    </td>
                    <td className="text-left px-6 py-4">
                      <button
                        onClick={() => handleEdit(category)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        Edit
                      </button>
                      {/* <button
                        onClick={() => handleDelete(category.category_id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button> */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 z-[120] bg-gray-500 bg-opacity-75 flex items-center justify-center overflow-y-auto p-4">
            <div className="bg-white rounded-lg p-6 sm:p-8 max-w-2xl w-full mx-auto my-8 max-h-[min(90dvh,100%)] overflow-y-auto">
              <h2 className="text-xl font-bold mb-6">
                {isEditing ? 'Edit Category' : 'Add New Category'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g. AC & Appliance Repair"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g. Fast, reliable repairs for AC & home appliances"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => setFormData({...formData, label: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g. NEW, QUICK etc."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showSubCategory"
                    checked={formData.showSubCategory}
                    onChange={(e) => setFormData({...formData, showSubCategory: e.target.checked})}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="showSubCategory" className="ml-2 block text-sm text-gray-900">
                    Show Subcategories
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                  {imagePreview && (
                    <div className="mb-4">
                      <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover rounded-md" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowImageSelectDialog(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {imagePreview ? 'Edit Image' : 'Upload Image'}
                  </button>
                </div>

                <div className="flex justify-end space-x-4 mt-8">
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowModal(false);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    {isEditing ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ImageSelectDialog
          open={showImageSelectDialog}
          onClose={() => setShowImageSelectDialog(false)}
          onFileProcessed={(file) => {
            handleImageSelect(file);
            setShowImageSelectDialog(false);
          }}
        />

        {showDetailsModal && selectedCategory && (
          <div className="fixed inset-0 z-[120] bg-gray-500 bg-opacity-75 flex items-center justify-center overflow-y-auto p-4">
            <div className="bg-white rounded-lg p-6 sm:p-8 max-w-2xl w-full mx-auto my-8 max-h-[min(90dvh,100%)] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Category Details</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <table className="w-full">
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="text-left font-black py-3 text-sm font-medium text-gray-500">Title</td>
                    <td className="text-left py-3 text-sm text-gray-900">{selectedCategory.title}</td>
                  </tr>
                  <tr>
                    <td className="text-left font-black py-3 text-sm font-medium text-gray-500">Subtitle</td>
                    <td className="text-left py-3 text-sm text-gray-900">{selectedCategory.subtitle || '-'}</td>
                  </tr>
                  <tr>
                    <td className="text-left font-black py-3 text-sm font-medium text-gray-500">Description</td>
                    <td className="text-left py-3 text-sm text-gray-900">{selectedCategory.description || '-'}</td>
                  </tr>
                  <tr>
                    <td className="text-left font-black py-3 text-sm font-medium text-gray-500">Label</td>
                    <td className="text-left py-3 text-sm text-gray-900">{selectedCategory.label || '-'}</td>
                  </tr>
                  <tr>
                    <td className="text-left font-black py-3 text-sm font-medium text-gray-500">Image</td>
                    <td className="text-left py-3 text-sm text-gray-900">
                      {selectedCategory.image ? (
                        <div>
                          <img 
                            src={selectedCategory.image} 
                            alt={selectedCategory.title}
                            className="w-32 h-32 object-cover rounded-md"
                          />
                          <a 
                            href={selectedCategory.image} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-800 text-sm ml-2"
                          >
                            View full image
                          </a>
                        </div>
                      ) : '-'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Category;
